import {
  Box,
  Button,
  Container,
  Dialog,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridFilterModel,
  GridPaginationModel,
  GridRowSelectionModel,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import { StatusType } from "../../app/constants";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { AGENTS } from "../../routes";
import { AgentType, GetAgentsFilters } from "./agentsAPI";
import {
  getAgents,
  selectAgents,
  selectAgentsStatus,
  selectTotalAgents,
} from "./agentsSlice";

import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

const columns: GridColDef[] = [
  { field: "name", headerName: "Nombre", width: 230 },
  { field: "country", headerName: "País", width: 230 },
  {
    field: "actions",
    type: "actions",
    width: 80,
    getActions: (params) => [
      <GridActionsCellItem
        icon={<ArrowForwardIcon />}
        label="Ver"
        onClick={() => window.open(`${AGENTS}?selected=${params.id}`, "_blank")}
      />,
    ],
    filterable: false,
    sortable: false,
  },
];

const AgentSelector = ({
  open,
  selectedAgent,
  toggleModal,
  onSelectExistentAgent,
  error,
  canUpdate,
}: {
  open: boolean;
  selectedAgent: AgentType | null;
  toggleModal: () => void;
  onSelectExistentAgent: (agent: AgentType) => void;
  error: boolean;
  canUpdate?: boolean;
}) => {
  const dispatch = useAppDispatch();

  const [pageModel, setPageModel] = useState({ page: 0, pageSize: 10 });
  const [currentAgent, setCurrentAgent] = useState<AgentType>();
  const [search, setSearch] = useState("");
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>();

  const loading = useAppSelector(selectAgentsStatus) === StatusType.loading;
  const agents = useAppSelector(selectAgents);
  const total = useAppSelector(selectTotalAgents);

  const onConfirm = () => {
    if (currentAgent) {
      onSelectExistentAgent(currentAgent);
      toggleModal();
    }
  };

  const onRowSelectionModelChange = (
    newRowSelectionModel: GridRowSelectionModel,
  ) => {
    if (newRowSelectionModel.length)
      setRowSelectionModel([
        newRowSelectionModel[newRowSelectionModel.length - 1],
      ]);
    else setRowSelectionModel([]);

    setCurrentAgent(
      agents.find(
        (agent) =>
          agent._id === newRowSelectionModel[newRowSelectionModel.length - 1],
      ),
    );
  };

  const getAgentsPage = (newFilters?: GetAgentsFilters) => {
    const filters: GetAgentsFilters = {
      page: newFilters?.page || pageModel.page + 1,
      pageSize: newFilters?.pageSize || pageModel.pageSize,
    };

    if (newFilters?.search !== undefined) filters.search = newFilters.search;
    else if (search) filters.search = search;

    dispatch(getAgents(filters));
  };

  const onFilterModelChange = (model: GridFilterModel) => {
    const newSearchTerm = model.quickFilterValues?.length
      ? model.quickFilterValues[0]
      : "";
    setSearch(newSearchTerm);
    getAgentsPage({
      search: newSearchTerm,
    });
  };

  const onPaginationModelChange = (model: GridPaginationModel) => {
    setPageModel({ page: model.page, pageSize: model.pageSize });
    getAgentsPage({ page: model.page + 1, pageSize: model.pageSize });
  };

  function CustomToolbar() {
    return (
      <GridToolbarContainer>
        <GridToolbarQuickFilter />
      </GridToolbarContainer>
    );
  }

  useEffect(() => {
    if (open) getAgentsPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <>
      <Paper
        elevation={1}
        sx={{ padding: 3, borderRadius: 5, margin: "20px 0" }}
      >
        <Typography variant="h6">Agente en origen *</Typography>
        {selectedAgent && selectedAgent?.name?.length > 0 ? (
          <Stack direction="row" sx={{ alignItems: "center" }}>
            <Typography variant="subtitle1" style={{ margin: "20px 0" }}>
              <strong>{selectedAgent.name}</strong> - {selectedAgent.country}
            </Typography>
          </Stack>
        ) : null}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: "10px",
          }}
        >
          {selectedAgent && selectedAgent._id && (
            <Button
              onClick={() =>
                window.open(`${AGENTS}?selected=${selectedAgent._id}`)
              }
              fullWidth
              variant="outlined"
            >
              Ver agente
            </Button>
          )}
          {canUpdate && (
            <Button
              onClick={toggleModal}
              fullWidth
              disableRipple
              variant="contained"
            >
              {selectedAgent?.name ? "Cambiar" : "Seleccionar"} agente
            </Button>
          )}
        </Box>
        {error && !selectedAgent && (
          <Typography
            sx={{
              color: "#d32f2f",
              fontSize: "0.75rem",
              fontWeight: 400,
              marginTop: "7px",
            }}
          >
            Por favor seleccione un agente
          </Typography>
        )}
      </Paper>
      <Dialog
        open={open}
        onClose={toggleModal}
        maxWidth="lg"
        sx={{
          "& .MuiDialog-paper": {
            width: { xs: "100%", sm: "80%", md: "60%" },
            maxWidth: "600px",
          },
        }}
      >
        <Container
          component="main"
          maxWidth="lg"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            justifyContent: "flex-start",
            marginTop: 4,
            marginBottom: 4,
          }}
        >
          <Typography variant="h6" marginBottom={2}>
            Seleccionar agente
          </Typography>

          <div style={{ height: 300, width: "100%" }}>
            <DataGrid
              filterMode="server"
              onFilterModelChange={onFilterModelChange}
              paginationMode="server"
              pagination={true}
              paginationModel={pageModel}
              onPaginationModelChange={onPaginationModelChange}
              loading={loading}
              rows={agents}
              columns={columns}
              disableColumnMenu
              rowCount={total}
              pageSizeOptions={[1, 10, 15]}
              disableColumnFilter
              disableColumnSelector
              disableDensitySelector
              onRowSelectionModelChange={onRowSelectionModelChange}
              rowSelectionModel={rowSelectionModel}
              slots={{ toolbar: CustomToolbar }}
              slotProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 500 },
                },
              }}
            />
          </div>

          <Box
            sx={{
              display: "flex",
              alignItems: "stretch",
              marginLeft: "auto",
              marginTop: 2,
            }}
          >
            <Button
              variant="text"
              sx={{ marginRight: 1 }}
              onClick={toggleModal}
            >
              Cancelar
            </Button>
            <Button
              disabled={!rowSelectionModel?.length}
              variant="contained"
              onClick={onConfirm}
            >
              Seleccionar agente
            </Button>
          </Box>
        </Container>
      </Dialog>
    </>
  );
};

export default AgentSelector;
